<template>
  <div class="exception-map-view" :style="{ height: height }">
    <!-- Map Container -->
    <div ref="mapContainer" class="map-container"></div>

    <!-- Weather Layer Picker -->
    <div v-if="showWeatherLayers && leafletLoaded" class="wx-layer-picker">
      <!-- Base layers: toggle between (only one active) -->
      <div class="wx-layer-group">
        <button
          v-for="layer in wxBaseLayerOptions" :key="layer.id"
          class="wx-layer-btn"
          :class="{ 'wx-layer-btn--active': activeWxLayer === layer.id, 'wx-layer-btn--off': activeWxLayer === 'none' && layer.id === 'none' }"
          :title="layer.label"
          @click="switchWxLayer(layer.id)"
        >
          <i :class="layer.icon"></i>
          <span class="wx-layer-btn__label">{{ layer.shortLabel }}</span>
        </button>
      </div>
      <!-- Overlay layers: individually toggleable (multiple can be active) -->
      <div class="wx-layer-divider"></div>
      <div class="wx-layer-group">
        <button
          v-for="layer in wxOverlayOptions" :key="layer.id"
          class="wx-layer-btn"
          :class="{ 'wx-layer-btn--active': activeOverlays[layer.id] }"
          :title="layer.label"
          @click="toggleOverlay(layer.id)"
        >
          <i :class="layer.icon"></i>
        </button>
      </div>
    </div>

    <!-- Day-Step Animation Controls -->
    <div v-if="allDates.length > 1" class="day-stepper">
      <div class="day-stepper-controls">
        <button class="day-stepper-btn" :class="{ 'day-stepper-btn--active': dayAutoPlay }" @click="toggleDayAutoPlay" :title="dayAutoPlay ? 'Stop' : 'Auto-play'">
          <i :class="dayAutoPlay ? 'bi bi-pause-fill' : 'bi bi-play-fill'"></i>
        </button>
        <button class="day-stepper-btn" :disabled="animationDayIndex <= 0 && !showAllDays" @click="stepDay(-1)" title="Previous day">
          <i class="bi bi-skip-backward-fill"></i>
        </button>
      </div>
      <div class="day-stepper-timeline">
        <button
          class="day-pill"
          :class="{ 'day-pill--active': showAllDays }"
          @click="toggleAllDays"
          title="Show all days"
        >
          <span class="day-pill-label">All</span>
        </button>
        <button
          v-for="(date, idx) in allDates" :key="date"
          class="day-pill"
          :class="{ 'day-pill--active': !showAllDays && animationDayIndex === idx, 'day-pill--risk': hasDayRisk(date) }"
          @click="jumpToDay(idx)"
          :title="formatShortDate(date)"
        >
          <span class="day-pill-label">{{ formatDayPill(date) }}</span>
          <span v-if="hasDayRisk(date)" class="day-pill-dot" :style="{ background: getDayRiskColor(date) }"></span>
        </button>
      </div>
      <div class="day-stepper-controls">
        <button class="day-stepper-btn" :disabled="animationDayIndex >= allDates.length - 1 && !showAllDays" @click="stepDay(1)" title="Next day">
          <i class="bi bi-skip-forward-fill"></i>
        </button>
      </div>
      <div v-if="hasAnyWxLayer" class="hour-slider">
        <input
          type="range"
          class="hour-slider-input"
          :min="0"
          :max="hourSliderSteps.length - 1"
          :value="hourSliderIndex"
          @input="onHourSliderChange($event.target.value)"
          :title="hourSliderLabel"
        />
        <div class="hour-slider-label">{{ hourSliderLabel }}</div>
      </div>
    </div>

    <!-- Legend -->
    <div class="map-legend">
      <div class="legend-item">
        <span class="legend-dot legend-ok"></span>
        <span class="legend-label">Clear</span>
      </div>
      <template v-if="legendCriteria.length">
        <div v-for="c in legendCriteria" :key="c.id" class="legend-item">
          <span class="legend-dot" :style="{ backgroundColor: getCriterionHex(c.color) }"></span>
          <span class="legend-label">{{ c.name || formatCriterionLabel(c) }}</span>
        </div>
      </template>
      <template v-else>
        <div class="legend-item">
          <span class="legend-dot legend-danger"></span>
          <span class="legend-label">{{ dangerLabel }}</span>
        </div>
      </template>
      <div v-if="showPending" class="legend-item">
        <span class="legend-dot legend-pending"></span>
        <span class="legend-label">Pending</span>
      </div>
    </div>

    <!-- Location Detail Popup -->
    <div 
      v-if="selectedLocation" 
      class="location-popup"
      :style="popupStyle"
    >
      <div class="popup-header">
        <strong>{{ selectedLocation.name || selectedLocation.location }}</strong>
        <button class="popup-close" @click="selectedLocation = null">&times;</button>
      </div>
      <div class="popup-body">
        <!-- Day-specific header when viewing a single day -->
        <div v-if="popupDayData" class="popup-day-header text-muted small mb-1">
          <i class="bi bi-calendar3 me-1"></i>{{ formatShortDate(selectedDay) }}
        </div>

        <div class="popup-status">
          <template v-if="popupCriteria.length">
            <span
              v-for="c in popupCriteria" :key="c.id"
              class="badge rounded-pill me-1"
              :style="{ backgroundColor: getCriterionHex(c.color), color: criterionTextColor(c.color) }"
            >{{ c.name || formatCriterionLabel(c) }}</span>
          </template>
          <span v-else class="badge rounded-pill bg-success">Clear</span>
        </div>
        
        <div v-if="selectedLocation.status === 'ok'" class="popup-details mt-2">
          <!-- Criteria field values summary -->
          <div v-if="criteria.length && popupCriteria.length" class="popup-criteria-values mt-1 mb-2">
            <div v-for="c in popupCriteria" :key="'cv-' + c.id" class="popup-criterion-row">
              <span class="popup-criterion-dot" :style="{ backgroundColor: getCriterionHex(c.color) }"></span>
              <span class="popup-criterion-name">{{ criterionLabel(c) }}</span>
              <span class="popup-criterion-val">{{ popupDayData ? getDayCriterionValue(popupDayData, c) : getCriterionWorstValue(selectedLocation, c) }}</span>
            </div>
          </div>
          <!-- Mini forecast preview (only when showing all days, not a single day) -->
          <div v-if="!selectedDay && selectedLocation.days?.length" class="forecast-preview mt-2">
            <div 
              v-for="day in selectedLocation.days.slice(0, 5)" 
              :key="day.datetime"
              class="forecast-day"
              :style="getDayPreviewStyle(day)"
              :title="day.datetime"
            >
              <div class="day-date">{{ formatShortDate(day.datetime) }}</div>
              <div class="day-temp">{{ Math.round(day.tempmax) }}°</div>
              <div v-if="getDayCriteria(day).length" class="day-criteria-dots">
                <span
                  v-for="dc in getDayCriteria(day)" :key="dc.id"
                  class="day-criteria-dot"
                  :style="{ backgroundColor: getCriterionHex(dc.color) }"
                  :title="dc.name + ': ' + getDayCriterionValue(day, dc)"
                ></span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="selectedLocation.status === 'error'" class="small text-danger mt-2">
          <i class="bi bi-x-circle me-1"></i>
          {{ selectedLocation.errorMessage }}
        </div>
        
        <button 
          v-if="selectedLocation.status === 'ok' && showViewDetails"
          class="btn btn-sm btn-outline-primary w-100 mt-2"
          @click="$emit('view-details', selectedLocation, { expandGrid: true })"
        >
          <i class="bi bi-box-arrow-up-right me-1"></i>View Full Report
        </button>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="map-loading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { WEATHER_FIELDS, formatCriterionLabel } from '../exceptionCriteriaPresets.js';

/**
 * ExceptionMapView Component
 * 
 * Displays location results on an interactive map with color-coded markers
 * based on exception severity (ok=green, warning=orange, danger=red).
 * 
 * Supports optional weather tile layers (precip, temp, wind, etc.) and
 * day-step animation to view per-day severity.
 * 
 * Uses Leaflet for mapping with OpenStreetMap tiles.
 */
import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn.js';

export default {
  name: 'ExceptionMapView',

  props: {
    results: {
      type: Array,
      default: () => []
    },
    /** Active criteria definitions (with id, name, color) */
    criteria: {
      type: Array,
      default: () => []
    },
    height: {
      type: String,
      default: '400px'
    },
    dangerLabel: {
      type: String,
      default: 'Critical'
    },
    showPending: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    initialCenter: {
      type: Array,
      default: () => [39.8283, -98.5795]
    },
    initialZoom: {
      type: Number,
      default: 4
    },
    /** Enable weather tile layer overlay */
    showWeatherLayers: {
      type: Boolean,
      default: false
    },
    /** Unit group for weather tiles: 'us' or 'metric' */
    unitGroup: {
      type: String,
      default: 'us'
    },
    /** When set, only these result locations are shown as full markers; others become subtle grey dots */
    filteredResultIds: {
      type: Array,
      default: null
    },
    /** When filtering by a specific criterion, its ID — used to color markers by that criterion only */
    criteriaFilterId: {
      type: String,
      default: null
    },
    /** The active severity filter type: 'all', 'ok', 'flagged' — controls filtered-out dot color */
    filterType: {
      type: String,
      default: 'all'
    },
    /** Whether to show the 'View Full Report' button in popups */
    showViewDetails: {
      type: Boolean,
      default: true
    }
  },

  emits: ['view-details', 'marker-click'],

  data() {
    return {
      map: null,
      markers: [],
      markerLayer: null,
      selectedLocation: null,
      selectedDay: null,
      popupPosition: { x: 0, y: 0 },
      leafletLoaded: false,
      L: null, // Leaflet reference

      // Weather layers — base layers (toggle between, only one active)
      wxBaseLayerOptions: [
        { id: 'none', label: 'No overlay', shortLabel: 'Off', icon: 'bi bi-x-lg' },
        { id: 'precip', label: 'Precipitation', shortLabel: 'Rain', icon: 'bi bi-cloud-rain-fill', element: 'precipcomposite', interval: 10 },
        { id: 'temp', label: 'Temperature', shortLabel: 'Temp', icon: 'bi bi-thermometer-half', element: 'temp', interval: 60 },
        { id: 'wind', label: 'Wind', shortLabel: 'Wind', icon: 'bi bi-wind', element: 'wind', interval: 60 },
      ],
      // Overlay layers (individually toggleable, multiple can be active simultaneously)
      wxOverlayOptions: [
        { id: 'alerts', label: 'Weather Warnings', icon: 'bi bi-exclamation-triangle-fill', element: 'alerts', interval: 60 },
        { id: 'hail', label: 'Hail', icon: 'bi bi-circle-fill', element: 'hail', interval: 10 },
        { id: 'lightning', label: 'Lightning', icon: 'bi bi-lightning-fill', element: 'lightning', interval: 10 },
      ],
      activeWxLayer: 'none',
      wxTileLayer: null,
      activeOverlays: {},    // { alerts: true, hail: false, lightning: true } etc.
      wxOverlayLayers: {},   // Leaflet layer instances keyed by overlay id

      // Day-step animation
      animationDayIndex: -1,  // -1 means "all days" (default overview)
      showAllDays: true,
      dayAutoPlay: false,
      dayAutoPlayTimer: null,
      hourSliderIndex: 0,
    };
  },

  computed: {
    popupStyle() {
      return {
        left: `${this.popupPosition.x}px`,
        top: `${this.popupPosition.y}px`
      };
    },

    /** Criteria shown in the legend — filtered when a KPI filter is active */
    legendCriteria() {
      if (this.criteriaFilterId) {
        const match = this.criteria.find(c => c.id === this.criteriaFilterId);
        return match ? [match] : this.criteria;
      }
      return this.criteria;
    },

    /** The day data for the selected day (when stepping through days), or null for all-days mode */
    popupDayData() {
      if (!this.selectedLocation || !this.selectedDay) return null;
      return this.selectedLocation.days?.find(d => d.datetime === this.selectedDay) || null;
    },

    /** Criteria to show in popup — day-specific or location-wide */
    popupCriteria() {
      if (!this.selectedLocation) return [];
      if (this.popupDayData) {
        return this.getDayCriteria(this.popupDayData);
      }
      return this.getLocationCriteria(this.selectedLocation);
    },

    /** Unique sorted dates across all results */
    allDates() {
      const dateSet = new Set();
      for (const result of this.results) {
        if (result.days) {
          for (const day of result.days) {
            dateSet.add(day.datetime);
          }
        }
      }
      return Array.from(dateSet).sort();
    },

    currentDayLabel() {
      if (this.showAllDays) return 'Now';
      if (this.animationDayIndex >= 0 && this.animationDayIndex < this.allDates.length) {
        return this.formatShortDate(this.allDates[this.animationDayIndex]);
      }
      return 'Now';
    },

    /** Time steps for the hour slider — 30 min for 'now', 3 hours for a specific day */
    hourSliderSteps() {
      if (this.showAllDays) {
        // Now mode: -2h to +2h in 15-min steps
        const steps = [];
        for (let m = -120; m <= 120; m += 15) {
          steps.push({ offsetMinutes: m, label: this.formatMinuteOffset(m) });
        }
        return steps;
      }
      // Specific day: 3-hour increments
      const steps = [];
      for (let h = 0; h < 24; h += 3) {
        const hh = String(h).padStart(2, '0');
        steps.push({ hour: h, label: `${hh}:00` });
      }
      return steps;
    },

    hourSliderLabel() {
      const step = this.hourSliderSteps[this.hourSliderIndex];
      return step ? step.label : '';
    },

    /** True when any weather layer (base or overlay) is active — controls hour slider visibility */
    hasAnyWxLayer() {
      if (this.activeWxLayer !== 'none') return true;
      return Object.values(this.activeOverlays).some(v => v);
    },

    /** Combined list of all layer configs for createWxLayer lookups */
    allWxLayerConfigs() {
      return [...this.wxBaseLayerOptions, ...this.wxOverlayOptions];
    },
  },

  watch: {
    results: {
      handler(newVal, oldVal) {
        const getKey = (r) => (r || []).map(x => x.id || x.location).sort().join('|');
        const locationChanged = getKey(newVal) !== getKey(oldVal);
        this.updateMarkers(locationChanged);
      },
      deep: true
    },
    filteredResultIds: {
      handler() {
        this.updateMarkers(false);
      },
      deep: true
    }
  },

  async mounted() {
    await this.loadLeaflet();
    this.initMap();
    // Default slider to center ("Now") in all-days mode
    this.hourSliderIndex = Math.floor(this.hourSliderSteps.length / 2);
    this.updateMarkers();
  },

  beforeUnmount() {
    this.stopDayAutoPlay();
    // Clean up overlay layers
    for (const layer of Object.values(this.wxOverlayLayers)) {
      if (layer && this.map) this.map.removeLayer(layer);
    }
    this.wxOverlayLayers = {};
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  methods: {
    /** Get a human-readable label for a criterion */
    criterionLabel(c) {
      if (c.name) return c.name;
      const fieldInfo = WEATHER_FIELDS[c.field];
      if (!fieldInfo) return c.field;
      const gran = c.granularity || 'daily';
      const prefix = (fieldInfo.section === 'both') ? (gran === 'hourly' ? 'Hourly ' : 'Daily ') : '';
      return prefix + fieldInfo.label;
    },

    async loadLeaflet() {
      if (this.showWeatherLayers) {
        // Use bundled leaflet so WxTileLayer shares the same L instance
        const leafletModule = await import('leaflet');
        await import('leaflet/dist/leaflet.css');
        await import('/src/weather-map/WxTileLayer.js');
        // Also load the alert vector layer factory
        this._alertVectorModule = await import('/src/weather-map/WxAlertVectorLayer.js');
        this.L = leafletModule.default;
      } else {
        // CDN loading for lightweight usage
        if (!window.L) {
          if (!document.querySelector('link[href*="leaflet.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
          }
          if (!document.querySelector('script[src*="leaflet.js"]')) {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }
        }
        this.L = window.L;
      }
      this.leafletLoaded = true;
    },

    initMap() {
      if (!this.leafletLoaded || !this.L || !this.$refs.mapContainer) return;

      const L = this.L;

      this.map = L.map(this.$refs.mapContainer, {
        center: this.initialCenter,
        zoom: this.initialZoom,
        zoomControl: true
      });

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map);

      // Create marker layer group
      this.markerLayer = L.layerGroup().addTo(this.map);

      // Set initial dateTime for weather tiles
      if (this.showWeatherLayers) {
        this.map.options.dateTime = new Date();
        this.map.options.dateTimeInterval = 60;
      }

      // Close popup on map click
      this.map.on('click', () => {
        this.selectedLocation = null;
      });
    },

    // ─── Weather tile layer methods ───

    createWxLayer(layerId) {
      const L = this.L;
      const config = this.allWxLayerConfigs.find(l => l.id === layerId);
      if (!config || !config.element) return null;

      // Alerts: hybrid — image tiles at zoom 1-7, GeoJSON vector at zoom 8+
      if (config.element === 'alerts') {
        return this._createHybridAlertLayer(L, config);
      }

      if (!L.TileLayer.WxTileLayer) return null;

      return new L.TileLayer.WxTileLayer({
        opacity: 0.55,
        level: 0,
        model: null,
        element: config.element,
        forceRefresh: true,
        key: VcGlobal.MAP_TILE_KEY,
        unitGroup: this.unitGroup,
        dateTimeInterval: config.interval,
      });
    },

    /** Create a hybrid alert layer: image tiles at zoom ≤7, GeoJSON vector at zoom ≥8 */
    _createHybridAlertLayer(L, config) {
      const group = L.layerGroup();
      group._isHybridAlert = true;

      // Image tile sub-layer
      const tileLayer = L.TileLayer.WxTileLayer
        ? new L.TileLayer.WxTileLayer({
            opacity: 0.55,
            level: 0,
            model: null,
            element: config.element,
            forceRefresh: true,
            key: VcGlobal.MAP_TILE_KEY,
            unitGroup: this.unitGroup,
            dateTimeInterval: config.interval,
          })
        : null;

      // GeoJSON vector sub-layer
      const vectorLayer = this._alertVectorModule
        ? this._alertVectorModule.createAlertVectorLayer(L, {
            key: VcGlobal.MAP_TILE_KEY,
            serverRoot: VcGlobal.TILE_SERVER_ROOT,
            opacity: 0.55,
            unitGroup: this.unitGroup,
            getDateTimeFn: () => this.map?.options?.dateTime || new Date(),
          })
        : null;

      group._alertTileLayer = tileLayer;
      group._alertVectorLayer = vectorLayer;
      group._alertZoomThreshold = 8; // vector at zoom >= 8

      // Switch handler
      const syncVisibility = () => {
        if (!this.map) return;
        const zoom = this.map.getZoom();
        const useVector = zoom >= group._alertZoomThreshold;

        if (useVector) {
          if (tileLayer && group.hasLayer(tileLayer)) group.removeLayer(tileLayer);
          if (vectorLayer && !group.hasLayer(vectorLayer)) group.addLayer(vectorLayer);
        } else {
          if (vectorLayer && group.hasLayer(vectorLayer)) group.removeLayer(vectorLayer);
          if (tileLayer && !group.hasLayer(tileLayer)) group.addLayer(tileLayer);
        }
      };

      // Wire up zoom events when added/removed from the map
      group.on('add', () => {
        syncVisibility();
        this.map.on('zoomend', syncVisibility);
      });
      group.on('remove', () => {
        this.map.off('zoomend', syncVisibility);
      });

      // Provide setAlertTime so updateWxDateTime can drive both sub-layers
      group.setAlertTime = (dt) => {
        if (vectorLayer && vectorLayer.setAlertTime) vectorLayer.setAlertTime(dt);
        // Tile layer picks up the map-level dateTime via the datetimeupdated event
      };
      group._isVectorAlert = true; // so _updateSingleLayerDateTime treats it correctly

      return group;
    },

    switchWxLayer(layerId) {
      if (this.activeWxLayer === layerId) return;
      this.activeWxLayer = layerId;

      // Remove old base layer
      if (this.wxTileLayer && this.map) {
        this.map.removeLayer(this.wxTileLayer);
        this.wxTileLayer = null;
      }

      if (layerId === 'none') return;

      // Ensure dateTime is set
      if (!this.map.options.dateTime) {
        this.map.options.dateTime = new Date();
      }

      this.wxTileLayer = this.createWxLayer(layerId);
      if (this.wxTileLayer) {
        this.map.addLayer(this.wxTileLayer);
        // Re-order overlays above the base layer
        this.reorderOverlays();
        this.updateWxDateTime();
      }
    },

    /** Toggle an overlay layer on/off (multiple can be active simultaneously) */
    toggleOverlay(layerId) {
      const isActive = !!this.activeOverlays[layerId];

      if (isActive) {
        // Remove overlay
        const layer = this.wxOverlayLayers[layerId];
        if (layer && this.map) {
          this.map.removeLayer(layer);
        }
        delete this.wxOverlayLayers[layerId];
        this.activeOverlays = { ...this.activeOverlays, [layerId]: false };
      } else {
        // Ensure dateTime is set
        if (!this.map.options.dateTime) {
          this.map.options.dateTime = new Date();
        }

        const layer = this.createWxLayer(layerId);
        if (layer) {
          this.map.addLayer(layer);
          this.wxOverlayLayers[layerId] = layer;
          this.activeOverlays = { ...this.activeOverlays, [layerId]: true };
          this.reorderOverlays();
          this.updateWxDateTime();
        }
      }
    },

    /** Ensure overlay layers are ordered above the base layer, in UI order */
    reorderOverlays() {
      // Re-add overlays in the order they appear in wxOverlayOptions
      // so they stack consistently: base → alerts → hail → lightning
      for (const opt of this.wxOverlayOptions) {
        const layer = this.wxOverlayLayers[opt.id];
        if (layer && this.map.hasLayer(layer)) {
          layer.bringToFront?.();
        }
      }
      // Keep markers on top of everything
      if (this.markerLayer) {
        this.markerLayer.bringToFront?.();
      }
    },

    // ─── Day-step animation methods ───

    stepDay(delta) {
      if (this.showAllDays) {
        // First step: go to first or last day
        this.showAllDays = false;
        this.animationDayIndex = delta > 0 ? 0 : this.allDates.length - 1;
      } else {
        const next = this.animationDayIndex + delta;
        if (next < 0 || next >= this.allDates.length) {
          // Wrap around during auto-play, stop at ends otherwise
          if (this.dayAutoPlay) {
            this.animationDayIndex = 0;
          }
          return;
        }
        this.animationDayIndex = next;
      }
      this.hourSliderIndex = this.showAllDays ? Math.floor(this.hourSliderSteps.length / 2) : 0;
      this.updateMarkers(false);
      this.updateWxDateTime();
    },

    toggleAllDays() {
      this.showAllDays = !this.showAllDays;
      if (this.showAllDays) {
        this.animationDayIndex = -1;
        this.stopDayAutoPlay();
        this.hourSliderIndex = Math.floor(this.hourSliderSteps.length / 2); // center on 'now'
      } else {
        this.animationDayIndex = 0;
        this.hourSliderIndex = 0;
      }
      this.updateMarkers(false);
      this.updateWxDateTime();
    },

    toggleDayAutoPlay() {
      if (this.dayAutoPlay) {
        this.stopDayAutoPlay();
      } else {
        this.dayAutoPlay = true;
        if (this.showAllDays) {
          this.showAllDays = false;
          this.animationDayIndex = 0;
          this.updateMarkers(false);
        }
        this.dayAutoPlayTimer = setInterval(() => {
          const next = this.animationDayIndex + 1;
          if (next >= this.allDates.length) {
            this.animationDayIndex = 0;
          } else {
            this.animationDayIndex = next;
          }
          this.updateMarkers(false);
          this.updateWxDateTime();
        }, 1500);
      }
    },

    stopDayAutoPlay() {
      this.dayAutoPlay = false;
      if (this.dayAutoPlayTimer) {
        clearInterval(this.dayAutoPlayTimer);
        this.dayAutoPlayTimer = null;
      }
    },

    /** Update WxTileLayer datetime based on day + hour slider */
    updateWxDateTime() {
      if (!this.map) return;

      let dt;
      if (this.showAllDays) {
        // "Now" mode — offset from current time by slider
        const step = this.hourSliderSteps[this.hourSliderIndex];
        dt = new Date();
        if (step && step.offsetMinutes) {
          dt = new Date(dt.getTime() + step.offsetMinutes * 60000);
        }
      } else {
        // Specific day — set hour based on slider
        const dateStr = this.allDates[this.animationDayIndex];
        if (!dateStr) return;
        const step = this.hourSliderSteps[this.hourSliderIndex];
        const hour = step ? (step.hour || 0) : 0;
        const hh = String(hour).padStart(2, '0');
        dt = new Date(dateStr + `T${hh}:00:00Z`);
      }

      this.map.options.dateTime = dt;

      // Update base layer
      if (this.wxTileLayer) {
        this._updateSingleLayerDateTime(this.wxTileLayer, dt);
      }

      // Update all active overlay layers
      for (const layer of Object.values(this.wxOverlayLayers)) {
        if (layer) {
          this._updateSingleLayerDateTime(layer, dt);
        }
      }
    },

    /** Helper: update a single wx layer's dateTime */
    _updateSingleLayerDateTime(layer, dt) {
      if (layer._isHybridAlert) {
        // Hybrid alert: update vector sub-layer directly, tile sub-layer via map event
        if (layer.setAlertTime) layer.setAlertTime(dt);
        this.map.fire('datetimeupdated', { dateTime: dt });
      } else if (layer._isVectorAlert && layer.setAlertTime) {
        layer.setAlertTime(dt);
      } else {
        this.map.fire('datetimeupdated', { dateTime: dt });
      }
    },

    onHourSliderChange(val) {
      this.hourSliderIndex = parseInt(val);
      this.updateWxDateTime();
    },

    formatMinuteOffset(m) {
      if (m === 0) return 'Now';
      const abs = Math.abs(m);
      const h = Math.floor(abs / 60);
      const mins = abs % 60;
      const sign = m < 0 ? '-' : '+';
      if (h > 0 && mins > 0) return `${sign}${h}h${String(mins).padStart(2,'0')}m`;
      if (h > 0) return `${sign}${h}h`;
      return `${sign}${mins}m`;
    },

    // ─── Marker methods ───

    updateMarkers(fitBounds = true) {
      if (!this.map || !this.markerLayer || !this.L) return;

      const L = this.L;

      // Clear existing markers
      this.markerLayer.clearLayers();
      this.markers = [];

      const bounds = [];
      const targetDate = (!this.showAllDays && this.animationDayIndex >= 0)
        ? this.allDates[this.animationDayIndex]
        : null;

      // Determine if a filter is active
      const hasFilter = Array.isArray(this.filteredResultIds);
      const activeSet = hasFilter ? new Set(this.filteredResultIds) : null;
      // When filtering by a specific criterion, find that criterion definition for its color
      const focusCriterion = this.criteriaFilterId
        ? this.criteria.find(c => c.id === this.criteriaFilterId)
        : null;

      for (const result of this.results) {
        const coords = this.getCoordinates(result);
        if (!coords) continue;

        bounds.push(coords);

        // Check if this result passes the current filter
        const isFiltered = hasFilter && !activeSet.has(result.location);

        // Determine color, label and whether this location has risk
        let markerColor, label, hasRisk = true;

        if (isFiltered) {
          // On 'ok' (No Risk) filter, locations with risk show grey; otherwise green
          const useGrey = this.filterType === 'ok' && this.getLocationCriteria(result).length > 0;
          markerColor = useGrey ? '#adb5bd' : '#198754';
          label = '';
          hasRisk = false;
        } else if (focusCriterion && targetDate && result.days) {
          // Criteria filter + specific day: check if the focused criterion fires on this day
          const dayData = result.days.find(d => d.datetime === targetDate);
          const dayMatches = dayData?.matchedCriteria?.includes(focusCriterion.id);
          if (dayMatches) {
            markerColor = this.getCriterionHex(focusCriterion.color);
            label = '';
            hasRisk = true;
          } else {
            // Criterion doesn't fire on this day — green
            markerColor = '#198754';
            label = '';
            hasRisk = false;
          }
        } else if (focusCriterion) {
          // Criteria filter, all-days view: show the focused criterion's color
          markerColor = this.getCriterionHex(focusCriterion.color);
          label = '';
          hasRisk = true;
        } else if (targetDate && result.days) {
          const dayData = result.days.find(d => d.datetime === targetDate);
          const dayCriteria = this.getDayCriteria(dayData);
          if (dayCriteria.length) {
            markerColor = this.getCriterionHex(dayCriteria[0].color);
            label = dayCriteria.length > 1 ? '+' + (dayCriteria.length - 1) : '';
          } else {
            markerColor = '#198754';
            label = '✓';
            hasRisk = false;
          }
        } else {
          const locCriteria = this.getLocationCriteria(result);
          if (locCriteria.length) {
            markerColor = this.getCriterionHex(locCriteria[0].color);
            label = locCriteria.length > 1 ? '+' + (locCriteria.length - 1) : '';
          } else {
            markerColor = '#198754';
            label = '✓';
            hasRisk = false;
          }
        }
        if (!isFiltered && (result.status === 'pending' || result.status === 'error')) {
          markerColor = '#6c757d';
          label = result.status === 'pending' ? '?' : '!';
          hasRisk = true; // show pending/error pins normally
        }

        // No-risk / filtered-out pins: small, muted
        const pinSize = hasRisk ? 30 : 12;
        const anchorY = hasRisk ? 42 : 6;
        const cssClass = hasRisk ? 'custom-marker' : (isFiltered ? 'custom-marker custom-marker--filtered' : 'custom-marker custom-marker--clear');
        const borderColor = isFiltered ? (markerColor === '#198754' ? '#15704a' : '#6c757d') : '';
        const markerIcon = L.divIcon({
          className: cssClass,
          html: `<div class="marker-pin${hasRisk ? '' : (isFiltered ? ' marker-pin--filtered' : ' marker-pin--clear')}" style="background-color: ${markerColor};${isFiltered ? 'border-color:' + borderColor + ';' : ''}">
                   ${hasRisk ? '<span class="marker-label">' + label + '</span>' : ''}
                 </div>`,
          iconSize: [pinSize, hasRisk ? 42 : pinSize],
          iconAnchor: [pinSize / 2, anchorY],
          popupAnchor: [0, -anchorY]
        });

        const marker = L.marker(coords, { icon: markerIcon, zIndexOffset: isFiltered ? -1000 : 0 });

        // All markers are clickable (including filtered-out grey dots)
        marker.on('click', (e) => {
          this.selectedLocation = result;
          this.selectedDay = targetDate || null;
          const containerPoint = this.map.latLngToContainerPoint(coords);
          this.popupPosition = {
            x: Math.min(containerPoint.x + 20, this.$refs.mapContainer.offsetWidth - 280),
            y: Math.max(containerPoint.y - 100, 10)
          };
          this.$emit('marker-click', result);
          e.originalEvent.stopPropagation();
        });

        this.markerLayer.addLayer(marker);
        this.markers.push({ marker, result });
      }

      // Fit bounds if we have markers and locations actually changed
      if (fitBounds && bounds.length > 0) {
        if (bounds.length === 1) {
          this.map.setView(bounds[0], 10);
        } else {
          this.map.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    },

    getCoordinates(result) {
      // Prefer top-level lat/lng (unified server shape)
      if (result.latitude !== undefined && result.longitude !== undefined) {
        return [result.latitude, result.longitude];
      }
      // Backward compat: weatherData wrapper
      if (result.weatherData) {
        const lat = result.weatherData.latitude;
        const lng = result.weatherData.longitude;
        if (lat !== undefined && lng !== undefined) {
          return [lat, lng];
        }
      }
      const location = result.location;
      const coordMatch = location.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
      if (coordMatch) {
        return [parseFloat(coordMatch[1]), parseFloat(coordMatch[2])];
      }
      return null;
    },

    getSeverityColor(severity, result) {
      if (result && result.status === 'pending') return '#6c757d';
      if (result && result.status === 'error') return '#6c757d';
      switch (severity) {
        case 'danger': return '#dc3545';
        case 'warning': return '#ffc107';
        default: return '#198754';
      }
    },

    /** Format a criterion for display when it has no custom name */
    formatCriterionLabel(c) {
      return formatCriterionLabel(c, this.unitGroup);
    },

    /** Map criterion color name to hex */
    getCriterionHex(color) {
      const hexMap = {
        danger: '#dc3545',
        warning: '#ffc107',
        info: '#3c76f2',
        success: '#198754',
        purple: '#6f42c1',
        orange: '#fd7e14',
        pink: '#d63384',
        teal: '#20c997',
        indigo: '#6610f2',
        cyan: '#0891b2',
        lime: '#84cc16'
      };
      return hexMap[color] || '#6c757d';
    },

    /** Text color for criterion badge: dark for light backgrounds */
    criterionTextColor(color) {
      const darkText = ['warning', 'lime', 'cyan'];
      return darkText.includes(color) ? '#212529' : '#fff';
    },

    /** Get unique triggered criteria for a location (across all its days) */
    getLocationCriteria(result) {
      if (!result.days?.length || !this.criteria.length) return [];
      const triggered = new Set();
      for (const day of result.days) {
        for (const cid of (day.matchedCriteria || [])) {
          triggered.add(cid);
        }
      }
      // Return in criteria definition order (first = highest priority)
      return this.criteria.filter(c => triggered.has(c.id));
    },

    /** Get matched criteria for a single day */
    getDayCriteria(day) {
      if (!day?.matchedCriteria?.length || !this.criteria.length) return [];
      const mcSet = new Set(day.matchedCriteria);
      // Return in criteria definition order (first = highest priority)
      return this.criteria.filter(c => mcSet.has(c.id));
    },

    /** Inline style for forecast day preview based on matched criteria */
    getDayPreviewStyle(day) {
      const dayCriteria = this.getDayCriteria(day);
      if (dayCriteria.length) {
        const hex = this.getCriterionHex(dayCriteria[0].color);
        return { backgroundColor: hex + '25', borderLeft: `3px solid ${hex}` };
      }
      return { backgroundColor: '#f8f9fa' };
    },

    getMarkerColor(result) {
      return this.getSeverityColor(result.maxSeverity, result);
    },

    getMarkerLabel(result) {
      if (result.status === 'pending') return '?';
      if (result.status === 'error') return '!';
      if (result.exceptionCount > 0) return result.exceptionCount;
      return '✓';
    },

    getStatusLabel(result) {
      if (result.status === 'pending') return 'Loading...';
      if (result.status === 'error') return 'Error';
      if (result.maxSeverity === 'danger') return this.dangerLabel;
      if (result.maxSeverity === 'warning') return 'Warning';
      return 'OK';
    },

    /** Get the worst (max or min, depending on operator) value for a criterion across a location's days */
    getCriterionWorstValue(result, criterion) {
      if (!result.days?.length) return '—';
      const field = criterion.field;
      if (field === 'alerts') {
        const count = result.days.reduce((sum, d) => sum + (d.matchedCriteria?.includes(criterion.id) ? 1 : 0), 0);
        return count + ' day' + (count !== 1 ? 's' : '');
      }
      const vals = result.days
        .filter(d => d[field] !== undefined && d[field] !== null)
        .map(d => d[field]);
      if (!vals.length) return '—';
      const isLow = ['lt', 'lte'].includes(criterion.operator);
      const worst = isLow ? Math.min(...vals) : Math.max(...vals);
      return typeof worst === 'number' ? (Number.isInteger(worst) ? worst : worst.toFixed(1)) : worst;
    },

    /** Get the value for a criterion's field for a specific day */
    getDayCriterionValue(day, criterion) {
      const field = criterion.field;
      if (field === 'alerts') return day.matchedCriteria?.includes(criterion.id) ? 'Alert' : '—';
      const val = day[field];
      if (val === undefined || val === null) return '—';
      return typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(1)) : val;
    },

    formatShortDate(datetime) {
      if (!datetime) return '';
      const parts = datetime.split('-');
      if (parts.length >= 3) {
        const d = new Date(datetime + 'T00:00:00');
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${dayNames[d.getDay()]} ${parseInt(parts[1])}/${parseInt(parts[2])}`;
      }
      return datetime;
    },

    /** Format a date for the day pill button (e.g. "Thu 12") */
    formatDayPill(datetime) {
      if (!datetime) return '';
      const parts = datetime.split('-');
      if (parts.length >= 3) {
        const d = new Date(datetime + 'T00:00:00');
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${dayNames[d.getDay()]} ${parseInt(parts[2])}`;
      }
      return datetime;
    },

    /** Jump directly to a day index */
    jumpToDay(idx) {
      this.showAllDays = false;
      this.animationDayIndex = idx;
      this.hourSliderIndex = 0;
      this.updateMarkers(false);
      this.updateWxDateTime();
    },

    /** Check if any result has a risk on a given date */
    hasDayRisk(date) {
      for (const result of this.results) {
        if (!result.days) continue;
        const day = result.days.find(d => d.datetime === date);
        if (day?.matchedCriteria?.length) return true;
      }
      return false;
    },

    /** Get the dominant risk color for a date across all results */
    getDayRiskColor(date) {
      for (const result of this.results) {
        if (!result.days) continue;
        const day = result.days.find(d => d.datetime === date);
        if (day?.matchedCriteria?.length) {
          const dayCriteria = this.getDayCriteria(day);
          if (dayCriteria.length) return this.getCriterionHex(dayCriteria[0].color);
        }
      }
      return '#dc3545';
    },

    selectLocation(locationStr) {
      const markerData = this.markers.find(m => m.result.location === locationStr);
      if (markerData) {
        this.selectedLocation = markerData.result;
        const coords = this.getCoordinates(markerData.result);
        if (coords) {
          this.map.setView(coords, Math.max(this.map.getZoom(), 8));
          const containerPoint = this.map.latLngToContainerPoint(coords);
          this.popupPosition = {
            x: Math.min(containerPoint.x + 20, this.$refs.mapContainer.offsetWidth - 280),
            y: Math.max(containerPoint.y - 100, 10)
          };
        }
      }
    },

    refreshMap() {
      if (this.map) {
        this.map.invalidateSize();
      }
    }
  }
};
</script>

<style scoped>
.exception-map-view {
  position: relative;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.map-container {
  width: 100%;
  height: 100%;
}

/* ─── Weather Layer Picker ─── */
.wx-layer-picker {
  position: absolute;
  top: 10px;
  right: 50px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 3px;
}

.wx-layer-group {
  display: flex;
  gap: 2px;
}

.wx-layer-divider {
  width: 1px;
  align-self: stretch;
  margin: 2px 3px;
  background: #ccc;
}

.wx-layer-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.wx-layer-btn:hover {
  background: #e9ecef;
  color: #212529;
}

.wx-layer-btn--active {
  background: #0d6efd;
  color: #fff;
}

.wx-layer-btn--active:hover {
  background: #0b5ed7;
  color: #fff;
}

.wx-layer-btn i {
  font-size: 12px;
}

.wx-layer-btn__label {
  display: none;
}

@media (min-width: 576px) {
  .wx-layer-btn__label {
    display: inline;
  }
}

/* ─── Day Stepper ─── */
.day-stepper {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(30, 30, 30, 0.88);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 5px 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  max-width: calc(100% - 24px);
}
.day-stepper-controls {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.day-stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.15s;
  font-size: 1rem;
}

.day-stepper-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.day-stepper-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.day-stepper-btn--active {
  background: #0d6efd;
  color: #fff;
}

.day-stepper-btn--active:hover {
  background: #0b5ed7;
  color: #fff;
}

.day-stepper-timeline {
  display: flex;
  align-items: center;
  gap: 3px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 4px;
}
.day-stepper-timeline::-webkit-scrollbar {
  display: none;
}

.day-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  position: relative;
  min-width: 38px;
}
.day-pill:hover {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}
.day-pill--active {
  background: rgba(13, 110, 253, 0.85);
  color: #fff;
}
.day-pill--active:hover {
  background: rgba(13, 110, 253, 1);
}
.day-pill-label {
  line-height: 1.1;
}
.day-pill-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ─── Hour Slider ─── */
.hour-slider {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  margin-left: 2px;
}
.hour-slider-input {
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  min-width: 80px;
}
.hour-slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  cursor: pointer;
}
.hour-slider-input::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  cursor: pointer;
}
.hour-slider-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  min-width: 42px;
  text-align: center;
}

/* ─── Legend ─── */
.map-legend {
  position: absolute;
  bottom: 62px;
  left: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  gap: 12px;
  font-size: 0.8rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-ok {
  background-color: #198754;
}

.legend-warning {
  background-color: #fd7e14;
}

.legend-danger {
  background-color: #dc3545;
}

.legend-pending {
  background-color: #6c757d;
}

.legend-label {
  color: #495057;
}

/* ─── Location Popup ─── */
.location-popup {
  position: absolute;
  width: 260px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  overflow: hidden;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.85rem;
}

.popup-close {
  border: none;
  background: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.popup-close:hover {
  color: #000;
}

.popup-body {
  padding: 10px 12px;
}

.popup-status {
  text-align: center;
}

.forecast-preview {
  display: flex;
  gap: 4px;
  justify-content: space-between;
}

.forecast-day {
  flex: 1;
  text-align: center;
  padding: 4px 2px;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 0.7rem;
}

.day-date {
  color: #6c757d;
}

.day-temp {
  font-weight: 600;
}

.day-criteria-dots {
  display: flex;
  gap: 2px;
  justify-content: center;
  margin-top: 2px;
}

.day-criteria-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

/* ─── Popup Criteria Values ─── */
.popup-criteria-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.72rem;
}

.popup-criterion-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.popup-criterion-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.popup-criterion-name {
  color: #495057;
  flex: 1;
  min-width: 0;
}

.popup-criterion-val {
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}

/* ─── Loading ─── */
.map-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
}

/* ─── Custom Markers ─── */
:deep(.custom-marker) {
  background: transparent;
  border: none;
}

:deep(.marker-pin) {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
  transition: background-color 0.3s;
}

:deep(.marker-label) {
  transform: rotate(45deg);
  font-size: 10px;
  font-weight: bold;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

:deep(.custom-marker--clear) {
  opacity: 0.8;
  cursor: pointer;
}

:deep(.marker-pin--clear) {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transform: none;
  box-shadow: none;
  border: 2px solid #15704a;
}

:deep(.custom-marker--filtered) {
  opacity: 0.8;
  cursor: pointer;
}

:deep(.marker-pin--filtered) {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transform: none;
  box-shadow: none;
  border: 2px solid #6c757d;
}
</style>
